import type { CardDTO, WalletItem } from "../../services/api/cardService";
import type { Translation } from "../../types/i18n";

/**
 * The card's material, shared by both faces (UX_CONCEPT.md §5.2, REQ-007 §4.1).
 *
 * PLUS and FREE cards are indigo; PREMIUM is dark navy with gold accents. The
 * card looks the same in light and dark mode — it is an object, not a surface —
 * so these values are deliberately not theme tokens.
 */
export interface CardMaterial {
  background: string;
  guilloche: string;
  accent: string;
  chip: string;
  stripe: string;
  ok: string;
  pending: string;
  tierLabel: string;
}

export function cardMaterial(
  tier: CardDTO["subscriptionTier"] | undefined,
): CardMaterial {
  if (tier === "PREMIUM") {
    return {
      background:
        "radial-gradient(120% 140% at 100% 0%, #24305e 0%, #121a3a 55%, #0b1029 100%)",
      guilloche: "rgba(241, 217, 160, 0.10)",
      accent: "#f1d9a0",
      chip: "linear-gradient(135deg, #f6e3b4, #c9a45a)",
      stripe: "#05081a",
      ok: "#f1d9a0",
      pending: "#ffcf85",
      tierLabel: "PREMIUM",
    };
  }
  return {
    background:
      "radial-gradient(120% 140% at 100% 0%, #5b47f0 0%, #3324d6 50%, #22179e 100%)",
    guilloche: "rgba(255, 255, 255, 0.08)",
    accent: "#c9c2ff",
    chip: "linear-gradient(135deg, #f3e2a4, #cfae52)",
    stripe: "#140d63",
    ok: "#8ef0c6",
    pending: "#ffcf85",
    tierLabel: tier === "PLUS" ? "PLUS" : "FREE",
  };
}

/** Physical card proportion, 85.6 × 54 mm. */
export const CARD_FRAME =
  "relative w-full max-w-[342px] aspect-[85.6/54] rounded-[18px] overflow-hidden select-none text-white";

export const CARD_SHADOW =
  "inset 0 1px 0 rgba(255,255,255,0.18), 0 18px 40px -12px rgba(30,20,140,0.55)";

/**
 * The wallet slot's name in the app language. The backend sends an English
 * `label`; the slot `id` is the stable code, so the label is looked up by it
 * and the backend's text is only the fallback for an unknown slot.
 */
const WALLET_LABEL_KEYS: Record<string, keyof Translation> = {
  emergency: "walletEmergency",
  medications: "walletMedications",
  vaccinations: "walletVaccinations",
  checkin: "walletCheckin",
  appointments: "walletAppointments",
  insurance: "walletInsurance",
  travel_docs: "walletTravelDocs",
  lab_results: "walletLabResults",
};

export function walletItemLabel(
  item: Pick<WalletItem, "id" | "type" | "label">,
  t: Translation,
): string {
  const key = WALLET_LABEL_KEYS[item.id] ?? WALLET_LABEL_KEYS[item.type];
  const value = key ? t[key] : undefined;
  return typeof value === "string" && value ? value : item.label;
}

/** "3 entries" / "1 entry" in the app language. */
export function walletEntryCount(count: number, t: Translation): string {
  return (count === 1 ? t.cardxEntryOne : t.cardxEntryMany).replace(
    "{n}",
    String(count),
  );
}
